import React, { useCallback, useState, useEffect } from 'react';

import Link from 'next/link';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useSWR from 'swr';

import { API_URL } from '@/lib/consts';
import { followsMap } from '@/lib/follows';

import { urlFetcher, DisplayUser, DisplayConceptRef } from '../lib/api';
import { FollowButton } from './FollowButton';

interface SuggestedFollowsProps {
  userInfo: DisplayUser;
  reloadUserInfo: () => void;
}

export const SuggestedFollows: React.FC<SuggestedFollowsProps> = ({ userInfo, reloadUserInfo }) => {
  const {
    data: suggestedFollows,
    error,
    isLoading,
  } = useSWR<DisplayUser[]>(`${API_URL}/feed/follows`, urlFetcher);

  const [ignoredUsers, setIgnoredUsers] = useState<Set<string>>(new Set());
  const follows = userInfo
    ? followsMap(userInfo.followed_users)
    : new Map<string, DisplayConceptRef[]>();

  const handleFollowUpdate = useCallback(() => {
    reloadUserInfo();
  }, [reloadUserInfo]);

  const handleIgnore = useCallback((username: string) => {
    setIgnoredUsers((prev) => {
      const newIgnoredUsers = new Set(prev);
      newIgnoredUsers.add(username);
      return newIgnoredUsers;
    });
  }, []);

  // Filter out followed and ignored users
  const filteredSuggestions =
    suggestedFollows?.filter(
      (user) => !follows.has(user.username) && !ignoredUsers.has(user.username)
    ) ?? [];

  if (isLoading) return <CircularProgress size={24} aria-label="Loading suggested follows" />;
  if (error) return <Alert severity="error">Error loading suggested follows.</Alert>;

  return (
    <Box role="region" aria-label="Suggested follows">
      {filteredSuggestions.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          {filteredSuggestions.slice(0, 5).map((user: DisplayUser, index: number) => (
            <React.Fragment key={user.username}>
              <MuiLink
                component={Link}
                href={`/citizen/${encodeURIComponent(user.username)}`}
                underline="hover"
                color="primary"
              >
                {user.username}
              </MuiLink>
              <Typography variant="body2" color="text.secondary" aria-label="Full name">
                {user.fullname}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <FollowButton
                  authUsername={userInfo.username}
                  followUsername={user.username}
                  currentlyFollowing={false} // We know it's false because of filtering
                  noTopicPrompt={true} // Don't prompt user to follow on specific topics
                  topics={[]}
                  onFollowUpdate={handleFollowUpdate}
                />
                <Button
                  onClick={() => handleIgnore(user.username)}
                  size="small"
                  variant="text"
                  aria-label={`Ignore suggestions for ${user.username}`}
                >
                  Ignore
                </Button>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No suggested follows at the moment.
        </Typography>
      )}
    </Box>
  );
};
